import React from 'react';
import './MessageBubble.scss';

const MessageBubble = ({ message, isOwn, showTime, unreadCount, isLastMessage }) => {
  // 하위 호환성: 'text' 우선, 없으면 'message' 사용
  const getMessageText = (msg) => {
    return msg.text || msg.message || '';
  };

  // 메시지 시간 포맷
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const messageDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return messageDate.toLocaleTimeString('ko-KR', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 메시지 타입에 따른 렌더링
  const renderMessageContent = () => {
    switch (message.messageType) {
      case 'image':
        return (
          <div className="message-image-container">
            {/* 이미지 데이터가 있는 경우 */}
            {message.images && message.images.length > 0 ? (
              <div className={`message-images ${message.images.length === 1 ? 'single' : 'multiple'}`}>
                {message.images.map((image, index) => (
                  <div key={index} className="image-wrapper">
                    <img 
                      src={image.url} 
                      alt={`Image ${index + 1}`}
                      onClick={() => handleImageClick(image.url)}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : (
              /* images 속성이 없는 경우 - 임시로 플레이스홀더 표시 */
              <div className="message-image-placeholder">
                <div className="placeholder-content">
                  <span>🖼️ 이미지</span>
                  <small>이미지 데이터를 불러올 수 없습니다</small>
                </div>
              </div>
            )}
            
            {/* 이미지와 함께 전송된 텍스트 메시지 */}
            {getMessageText(message) && getMessageText(message).trim() && (
              <div className="image-message-text">
                {renderTextWithLinks(getMessageText(message))}
              </div>
            )}
          </div>
        );
      
      case 'file':
        return (
          <div className="message-file">
            <div className="file-icon">
              {message.fileData?.type?.startsWith('image/') ? '🖼️' :
                message.fileData?.type?.includes('pdf') ? '🖼️' :
                message.fileData?.type?.includes('word') ? '📝' :
                message.fileData?.type?.includes('zip') ? '🗜️' : '📄'}
            </div>
            <div className="file-info">
              <span className="file-name">{getMessageText(message)}</span>
              {message.fileData?.size && (
                <span className="file-size">
                용량: {(message.fileData.size / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
              {message.fileData?.url && (
                <a 
                  href={message.fileData.url} 
                  download={message.fileData.name || getMessageText(message)}
                  className="download-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  다운로드
                </a>
              )}
            </div>
          </div>
        );
      
      case 'system':
        return (
          <div className="system-message">
            {getMessageText(message)}
          </div>
        );
      
      default:
        return (
          <div className="message-text">
            {/* 텍스트에서 링크를 감지하여 클릭 가능하게 만들기 */}
            {renderTextWithLinks(getMessageText(message))}
          </div>
        );
    }
  };

  // 이미지 클릭 핸들러 (확대보기)
  const handleImageClick = (imageUrl) => {
    // 이미지 확대보기 기능을 추가할 수 있습니다
    window.open(imageUrl, '_blank');
  };

  // 텍스트에서 URL, 이메일, 도메인을 감지하여 링크로 변환
  const renderTextWithLinks = (text) => {
    // 통합 정규식으로 모든 링크 패턴을 찾기
    const linkRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|([a-zA-Z0-9-]+\.[a-zA-Z0-9.-]*[a-zA-Z]{2,})/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // 매치 이전의 텍스트 추가
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      const fullMatch = match[0];
      const url = match[1];
      const email = match[2];
      const domain = match[3];
      
      if (url) {
        // URL
        parts.push({
          type: 'url',
          content: fullMatch
        });
      } else if (email) {
        // 이메일
        parts.push({
          type: 'email',
          content: fullMatch
        });
      } else if (domain && !email) {
        // 도메인 (이메일이 아닌 경우)
        parts.push({
          type: 'domain',
          content: fullMatch
        });
      } else {
        parts.push(fullMatch);
      }
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // 남은 텍스트 추가
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.map((part, index) => {
      if (typeof part === 'object') {
        switch (part.type) {
          case 'url':
            return (
              <a 
                key={index} 
                href={part.content} 
                target="_blank" 
                rel="noopener noreferrer"
                className="message-link"
              >
                {part.content}
              </a>
            );
          case 'email':
            return (
              <a 
                key={index} 
                href={`mailto:${part.content}`}
                className="email-link"
              >
                {part.content}
              </a>
            );
          case 'domain':
            return (
              <a 
                key={index} 
                href={`https://${part.content}`}
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  textDecorationColor: '#0066cc'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#0052a3';
                  e.target.style.textDecorationColor = '#0052a3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#0066cc';
                  e.target.style.textDecorationColor = '#0066cc';
                }}
                className="message-link"
              >
                {part.content}
              </a>
            );
          default:
            return part.content;
        }
      }
      return part;
    });
  };

  // 시스템 메시지인 경우 특별한 스타일 적용
  if (message.messageType === 'system') {
    return (
      <div className="message-bubble system">
        {renderMessageContent()}
      </div>
    );
  }

  return (
    <div className={`message-bubble ${isOwn ? 'own' : 'other'}`}>
      <div className={`message-content ${message.messageType === 'image' ? 'image-message' : ''} ${message.messageType === 'file' ? 'file-message' : ''}`}>
        {renderMessageContent()}
      </div>
      
      {showTime && (
        <div className="message-time">
          {isOwn && unreadCount > 0 && (
            <span className="unread-count">1</span>
          )}
          <span className="time-text">{formatMessageTime(message.timestamp)}</span>
        </div>
      )}
    </div>
  );
};

// React.memo로 불필요한 리렌더링 방지
export default React.memo(MessageBubble);